// src/app/service/inactivity.service.ts
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { CountDownComponent } from '../COMPONENT/count-down/count-down.component';
import { BehaviorSubject } from 'rxjs';

export type SessionEndState = 'idle' | 'ending' | 'ended' | 'error';

@Injectable({ providedIn: 'root' })
export class InactivityService {
  private readonly sessionEndSubject = new BehaviorSubject<SessionEndState>('idle');
  readonly sessionEndState$ = this.sessionEndSubject.asObservable();
  private timeoutHandle: any | null = null;
  private countdownHandle: any | null = null;
  private listenersInitialized = false;
  private countdownDialogRef: MatDialogRef<CountDownComponent> | null = null;

  /**
   * Only run the idle timer while a user is actually signed in. Without
   * this, the timer -- armed once at app bootstrap via AppComponent's
   * constructor injection -- keeps running for the life of the tab
   * regardless of login state: any mouse move or keypress on the public
   * login page re-arms it, so the "Session Timeout" dialog (and the
   * sign-out it triggers) kept firing on a loop even after the user was
   * already signed out.
   */
  private authenticated = false;

  // ✅ real 2 minutes (120000 ms)
  private readonly timeoutDuration = 2 * 60 * 1000;
  private readonly countdownDuration = 60; // visible warning; idle policy unchanged

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private auth: AngularFireAuth,
  ) {
    this.initActivityListener();

    this.auth.authState.subscribe(user => {
      const wasAuthenticated = this.authenticated;
      this.authenticated = !!user;

      if (this.authenticated && !wasAuthenticated) {
        this.sessionEndSubject.next('idle');
        this.startTimeout();
      } else if (!this.authenticated && wasAuthenticated) {
        this.clearAllTimers();
        this.countdownDialogRef?.close(false);
      }
    });
  }

  initActivityListener(): void {
    if (this.listenersInitialized) return;
    this.listenersInitialized = true;

    ['mousemove', 'keydown', 'click', 'touchstart', 'input', 'pointerdown', 'scroll'].forEach(evt => {
      window.addEventListener(evt, () => this.resetTimeout(), { passive: true });
    });
  }

  private startTimeout(): void {
    if (!this.authenticated || this.sessionEndSubject.value !== 'idle') return;
    this.clearAllTimers();
    this.timeoutHandle = setTimeout(() => this.startCountdown(), this.timeoutDuration);
  }

  private resetTimeout(): void {
    if (!this.authenticated || this.sessionEndSubject.value !== 'idle') return;
    if (this.countdownDialogRef) {
      this.countdownDialogRef.close(true);
      return;
    }
    this.startTimeout();
  }

  private clearAllTimers(): void {
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
    if (this.countdownHandle) {
      clearInterval(this.countdownHandle);
      this.countdownHandle = null;
    }
  }

  private startCountdown(): void {
    if (!this.authenticated) return;
    let remaining = this.countdownDuration;
    const total = this.countdownDuration;

    const dialogRef = this.dialog.open(CountDownComponent, {
      disableClose: true,
      data: { countdown: remaining, totalSeconds: total },
    });
    this.countdownDialogRef = dialogRef;

    // ✅ ensure UI updates each tick (NgZone.run)
    this.clearAllTimers();
    this.countdownHandle = setInterval(() => {
      remaining--;

      this.ngZone.run(() => {
        if (dialogRef.componentInstance?.data) {
          dialogRef.componentInstance.data.countdown = remaining;
        }
      });

      if (remaining <= 0) {
        this.clearAllTimers();
        dialogRef.close(false); // auto sign-out
      }
    }, 1000);

    dialogRef.afterClosed().subscribe(keepAlive => {
      this.countdownDialogRef = null;
      this.clearAllTimers();
      if (!this.authenticated) return; // already signed out elsewhere; nothing to do
      if (keepAlive) {
        this.resetTimeout();   // user chose to stay
      } else {
        this.logoutUser();     // user allowed timeout or clicked Sign out
      }
    });
  }

  private logoutUser(): void {
    this.endSession();
  }

  /** Hide the clinical workspace immediately; never report sign-out success before Firebase confirms it. */
  endSession(): void {
    if (['ending', 'ended'].includes(this.sessionEndSubject.value)) return;
    this.sessionEndSubject.next('ending');
    this.clearAllTimers();
    window.dispatchEvent(new Event('jade-session-expiring'));
    this.dialog.closeAll();
    // Retain the existing timeout cleanup policy. A browser storage error must not prevent sign-out.
    try { localStorage.clear(); } catch { /* Storage may be disabled. */ }
    try { sessionStorage.clear(); } catch { /* Storage may be disabled. */ }
    this.ngZone.run(() => void this.router.navigate(['/session-expired'], {replaceUrl: true}));
    void this.auth.signOut().then(
      () => this.ngZone.run(() => this.sessionEndSubject.next('ended')),
      () => this.ngZone.run(() => this.sessionEndSubject.next('error')),
    );
  }
}
